import { Button, Col, Form, Row, Space, theme } from "antd";
import { useEffect, useState } from "react";
import Data from "../controls/Data";
import { DataRequest } from "../controls/Data.default";
import { QueryOperation, SearchQuery } from "../code/search-query/search-query";
import {
  employeesViewColumns,
  mapEmployeesViewQuery,
} from "./CompanyEmployeesView.default";
import {
  getCompanies,
  getCompanyEmployees,
} from "../repository/data.repository";
import { Company } from "../repository/data.default";
import { retry } from "../utils/http";
import { nameOf } from "../utils/typescript";
import DynamicForm from "../controls/DynamicForm";

// DUMMY DATA

export interface CompanyEmployeesViewProps {
  readonly trigger?: string;
}

function CompanyEmployeesView(props: CompanyEmployeesViewProps) {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);

  const [loading, setLoading] = useState(false);
  const [context, setContext] = useState<{
    readonly searchQuery: SearchQuery;
  }>({
    searchQuery: {
      conditions: [],
    },
  });
  const [valuesValid, setValuesValid] = useState(false);

  useEffect(() => {
    setValuesValid(!form.getFieldsError().some(({ errors }) => errors.length));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  useEffect(() => {
    setContext((prev) => ({ ...prev }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.trigger]);

  function onLoading(loading: boolean): void {
    setLoading(loading);
  }

  function onSearchData(): void {
    const searchQuery = mapEmployeesViewQuery(values);
    setContext({
      searchQuery,
    });
  }

  function onClearData(): void {
    setValuesValid(true);
    form.resetFields();
  }

  const request = ({ context, pageNumber, pageSize, sort }: DataRequest) => {
    const sortBy = (sort || [])[0]?.key ?? null;
    const sortDescending = (sort || [])[0]?.direction === "desc";

    return getCompanyEmployees({
      ...context,
      pageNumber: 0,
      pageSize: 100,
      ...(sortBy
        ? {
            sortBy,
            sortDescending,
          }
        : {}),
    }).then(({ data, totalRecords }) => ({
      data,
      pageNumber,
      pageSize,
      total: totalRecords,
    }));
  };

  const companiesRequest = (search: string) => {
    return retry(() =>
      getCompanies({
        searchQuery: {
          conditions: [
            {
              field: nameOf<Company>("name"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((dataItem) => ({
        display: dataItem.name,
        value: dataItem.uid,
        dataItem,
      })),
    );
  };

  return (
    <Row className="flex flex-auto flex-row flex-nowrap overflow-hidden">
      {/* Form */}
      <Col
        className="mr-4 flex flex-col border border-solid pl-2 pr-2"
        style={{
          borderColor: token.colorBorder,
          borderRadius: token.borderRadius,
        }}
      >
        <Space className="flex flex-row justify-end pb-2 pt-2">
          <Button disabled={!valuesValid || loading} onClick={onClearData}>
            Clear
          </Button>
          <Button
            type="primary"
            loading={loading}
            disabled={!valuesValid}
            onClick={onSearchData}
          >
            Search
          </Button>
        </Space>
        <DynamicForm
          form={form}
          schema={{
            groups: [
              {
                controls: [
                  {
                    type: "combobox",
                    name: "company",
                    label: "Company",
                    request: companiesRequest,
                  },
                  {
                    type: "textbox",
                    name: "userName",
                    label: "User Name",
                  },
                  {
                    type: "textbox",
                    name: "firstName",
                    label: "First Name",
                  },
                  {
                    type: "textbox",
                    name: "middleName",
                    label: "Middle Name",
                  },
                  {
                    type: "textbox",
                    name: "lastName",
                    label: "Last Name",
                  },
                  {
                    type: "datepicker",
                    name: "dateOfBirth",
                    label: "Date Of Birth",
                  },
                  {
                    type: "textbox",
                    name: "email",
                    label: "Email",
                  },

                  {
                    type: "textbox",
                    name: "phoneNumber",
                    label: "Phone",
                  },
                ],
              },
            ],
          }}
        />
      </Col>
      {/* Data */}
      <Data
        columns={employeesViewColumns}
        context={context}
        pageSize={100}
        request={request}
        rowKey={({ uid }) => uid}
        onLoading={onLoading}
      />
    </Row>
  );
}

export default CompanyEmployeesView;
